import $ from "jquery";

import store from "../store";
import ActionTypes from "./actionTypes";
import {updateCurrentQuote} from "./quotesFunctions";
import {getCompressedCartItem} from "./backendFunctions";
import {setLocalStorageState} from "./localStorageState";
import {getPriceWorkSheetSaveValues} from "../bvcomponents/priceWorksheet/discountFunctions";
import {
  LocalStorageJobData,
  RestoringJobDiscountSection,
  RestoringJobFeature,
  RestoringJobRecord,
  RestoringJobRecordData,
} from "../interfaces/RestoringJobData";
import {IS_LIXIL_UI} from "../config";
import {LOOK_OVERLAYS} from "../bvcomponents/look/core/constants";
import {getCurrentLayout} from "../bvcomponents/appLayout";

/**
 * The session is kept in the localStorage in the same format as the backend returns a saved job
 * (see RestoringJobData), so it is restored by the same `parseJobIdResponse` - with all rooms, cart,
 * wizard values, price worksheet etc.
 *
 * Saving never blocks the UI:
 * 1) calls are debounced - a burst of updates (selecting features, adding to cart...) is written once
 * 2) the data is collected and written when the browser is idle (requestIdleCallback), in 2 separate tasks
 * 3) nothing is written when nothing changed
 * 4) the pending update is flushed when the page is hidden/closed, so it is not lost
 */

export const LOCAL_STORAGE_JOB_VERSION = 1;

const SAVE_DEBOUNCE_MS = 1000;
const IDLE_TIMEOUT_MS = 3000; // the save runs at the latest after this time, even if the browser is busy
const MIN_RAW_BASE64_LENGTH = 1024;
const RAW_BASE64_REGEX = /^[A-Za-z0-9+/=\r\n]+$/;

let debounceTimer: number | null = null;
let saveGeneration = 0; // a newer save cancels the older one that is still in progress
let lastSavedValue = "";
let saveBlocked = false;
let pageHideListenersAdded = false;

// ---------------------------------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Room bvState + job properties, exactly as they are saved in the job (record.data).
 * Shared with getSaveQuoteFastData, so the job and the localStorage formats can't drift apart
 */
export const getJobRecordData = (quoteObject: any, state: any): RestoringJobRecordData => {
  const {quotesState, contractSigningState, printState, priceWorksheetState, paymentState} = state;
  return Object.assign({}, quoteObject.data, {
    activeQuoteIndex: quotesState.activeQuoteIndex,
    contractSigningValues: contractSigningState.pagesCustomFieldsValue,
    contractsValues: contractSigningState.contractsValues,
    savedInvoiceNumberOfPages: contractSigningState.savedInvoiceNumberOfPages,
    needToUpdateContract: contractSigningState.needToUpdateContract,
    receiptSignature: printState.receiptSignature,
    receiptWorkOrderNumber: printState.receiptWorkOrderNumber,
    receiptHDLeadNumber: printState.receiptHDLeadNumber,
    receiptBathroomLocation: printState.receiptBathroomLocation,
    receiptEstimateStart: printState.receiptEstimateStart,
    receiptEstimatedText: printState.receiptEstimatedText,
    priceWorkSheetLogic: getPriceWorkSheetSaveValues(priceWorksheetState),
    roomMultiplier: quoteObject.roomMultiplier || 1,
    shippingAddress: paymentState.shippingAddress,
    billingAddress: paymentState.billingAddress,
    billingSameAsShipping: paymentState.billingSameAsShipping,
  });
};

/**
 * the cart item in the format the backend returns it (fabricatorFeatureId -> fabricatorFeature_id, featureId -> feature_id)
 */
const getRestoringJobFeature = (cartItem: any): RestoringJobFeature | null => {
  const compressed = getCompressedCartItem(cartItem);
  if (!compressed) {
    return null
  }
  const {fabricatorFeatureId, featureId, ...rest} = compressed;
  return {
    ...rest,
    quote_id: null,
    fabricatorFeature_id: fabricatorFeatureId,
    feature_id: featureId,
    noteText: compressed.noteText === undefined ? null : compressed.noteText,
  };
};

/**
 * only selected discounts, the same as getSaveQuoteFastData does
 */
const getSelectedDiscountSections = (discountSections: any[] = []): RestoringJobDiscountSection[] => {
  return discountSections
    .map(discountSection => Object.assign({}, discountSection, {
      discounts: discountSection.discounts
        .filter(discount => discount.selected)
        .map(discount => Object.assign({}, discount, {oldDeleted: undefined})),
    }))
    .filter(discountSection => discountSection.discounts.length);
};

/**
 * the backend lower-cases the first letter of the IME keys, restoreIMEObjectKeys reverts it
 */
const getIMEFieldsValues = (contractIMEFieldsValues: any): Array<Record<string, unknown>> => {
  if (!Array.isArray(contractIMEFieldsValues)) {
    return []
  }
  return contractIMEFieldsValues.map(IMEObject => {
    return Object.keys(IMEObject || {}).reduce((acc, key) => {
      acc[key.charAt(0).toLowerCase() + key.slice(1)] = IMEObject[key];
      return acc
    }, {} as Record<string, unknown>)
  });
};

/**
 * Collects the job data for the localStorage. No images are generated here (it's cheap)
 */
export const getJobDataForLocalStorage = async (): Promise<LocalStorageJobData> => {
  const quotes = await updateCurrentQuote(null, undefined, true); // forLocalStorage = no canvas image, no sqft images
  const state = store.getState();
  const {cartState, priceWorksheetState, contractSigningState} = state;

  const records: RestoringJobRecord[] = quotes.map((quoteObject, quoteIndex) => ({
    id: quoteObject.quoteId || null,
    room: quoteObject.room,
    data: getJobRecordData(quoteObject, state),
    sqFtCalcData: quoteObject.sqFtCalcData || null,
    features: cartState.cartItems
      .filter(ci => ci.cartFeature && ci.cartFeature.dbFeature && (ci.currentQuote === quoteIndex))
      .map(getRestoringJobFeature)
      .filter(Boolean),
  }));

  return {
    localStorageVersion: LOCAL_STORAGE_JOB_VERSION,
    savedAt: Date.now(),
    discountSections: getSelectedDiscountSections(priceWorksheetState.discountSections),
    jobMarkUp: JSON.stringify(cartState.markUpLogic),
    imeFieldsValues: getIMEFieldsValues(contractSigningState.contractIMEFieldsValues),
    records,
  };
};

// ---------------------------------------------------------------------------------------------------------------------
// Base64
// ---------------------------------------------------------------------------------------------------------------------

/**
 * base64 images (sqft canvas/slab/3D images, signatures...) and blob urls can't be kept, they fill the
 * localStorage quota in no time, and blob urls are dead after reload anyway
 */
export const isBinaryStringValue = (value: string): boolean => {
  if (!value) {
    return false
  }
  if (value.startsWith("data:") || value.startsWith("blob:")) {
    return true
  }
  return value.length >= MIN_RAW_BASE64_LENGTH && RAW_BASE64_REGEX.test(value.slice(0, MIN_RAW_BASE64_LENGTH));
};

/**
 * JSON.stringify replacer: replaces base64 strings with an empty string. It doesn't mutate the objects
 * (they come from the redux store), and it's done in the same pass as stringify, without a deep copy
 */
const base64Replacer = (key: string, value: unknown) => {
  return (typeof value === "string" && isBinaryStringValue(value)) ? "" : value;
};

export const stringifyJobDataForLocalStorage = (jobData: LocalStorageJobData): string => {
  return JSON.stringify(jobData, base64Replacer);
};

// ---------------------------------------------------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------------------------------------------------

const runWhenIdle = (callback: () => void) => {
  const win = window as any;
  if (typeof win.requestIdleCallback === "function") {
    win.requestIdleCallback(callback, {timeout: IDLE_TIMEOUT_MS});
  } else {
    window.setTimeout(callback, 0);
  }
};

const waitForIdle = () => new Promise<void>(resolve => runWhenIdle(resolve));

/**
 * The session is tied to the visualizer the page was loaded with (serverState.initialVisualizerType), not to the
 * current one: the visualizer can be switched during the session (bathroom -> kitchen), but after the reload the
 * initial visualizer is loaded again and has to find the session.
 * The key is the initial visualizer layout key (bvLookState, kvSceneState...), so already stored sessions still work
 */
export const getJobLocalStorageKey = (): string => {
  const initialVisualizerType = store.getState().serverState.initialVisualizerType
    || (document as any).kvConfig.visualizerType;
  const initialLayout = getCurrentLayout(initialVisualizerType);
  return initialLayout && initialLayout.localStorageKey
    ? initialLayout.localStorageKey
    : `${initialVisualizerType}State`;
};

const canSaveToLocalStorage = () => !saveBlocked && !(document as any).kvConfig.jobId;

const saveNow = async (useIdleTime: boolean) => {
  const generation = ++saveGeneration;
  const isOutdated = () => generation !== saveGeneration || !canSaveToLocalStorage();
  try {
    if (useIdleTime) {
      await waitForIdle();
    }
    if (isOutdated()) {
      return
    }
    const jobData = await getJobDataForLocalStorage();

    if (useIdleTime) {
      await waitForIdle(); // stringify in a separate task
    }
    if (isOutdated()) {
      return
    }
    const value = stringifyJobDataForLocalStorage({...jobData, savedAt: 0});
    if (value === lastSavedValue) {
      return // nothing changed
    }
    // savedAt is not compared, otherwise every save would be different
    setLocalStorageState(getJobLocalStorageKey(), value.replace('"savedAt":0', `"savedAt":${jobData.savedAt}`));
    lastSavedValue = value;
  } catch (ex) {
    console.log("localStorage exception", ex);
  }
};

const flushPendingSave = () => {
  if (debounceTimer === null) {
    return
  }
  window.clearTimeout(debounceTimer);
  debounceTimer = null;
  if (canSaveToLocalStorage()) {
    saveNow(false); // the page is going away - don't wait for idle time
  }
};

const addPageHideListeners = () => {
  if (pageHideListenersAdded) {
    return
  }
  pageHideListenersAdded = true;
  window.addEventListener("pagehide", flushPendingSave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPendingSave();
    }
  });
};

/**
 * Schedules the localStorage update. Returns immediately, the data is written in the background
 */
const scheduleJobLocalStorageSave = () => {
  if (!canSaveToLocalStorage()) {
    return
  }
  addPageHideListeners();
  if (debounceTimer !== null) {
    window.clearTimeout(debounceTimer);
  }
  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    saveNow(true);
  }, SAVE_DEBOUNCE_MS);
};

/**
 * Stores the whole session (all rooms/visualizers) to the localStorage, in the same format as the job is saved
 * (see RestoringJobData), so it's restored the same way (see restoreJobFromLocalStorage).
 * It's cheap to call it often: calls are debounced and the save is done in the background (browser idle time),
 * so it never blocks the UI. Not for saved jobs (document.kvConfig.jobId)
 */
export const updateLocalStorage = () => {
  const {lookState, serverState} = store.getState();
  if (IS_LIXIL_UI && ((lookState.lookOverlayOpened && (lookState.lookOverlayOpened !== LOOK_OVERLAYS.aiPanel))
    || !serverState.visualizersLoaded.length)) {
    return // prevent updating localStorage on restore question popup
  }
  scheduleJobLocalStorageSave();
};

/**
 * Instead of calling updateLocalStorage after every change, we watch the signals every change already sends:
 * 1) cartState.cartItems (ADD_TO_CART, CHANGE_CART_QUANTITY, CHANGE_CART_FEATURE, REMOVE_FROM_CART, RESTORE_CART...)
 * 2) wizardState.allWizardsValues (updateAllWizardValues, SET_WIZARD_SELECTED_VALUES, SET_WIZARD_ACTIVE_ITEM...)
 * 3) bathroomVisualizerState.canvasCount (updateCanvasCount) and the legacy CHANGE_LAYOUT / UPDATE_ALL_ITEMS window
 *    events - every feature change redraws the canvas through one of them (see useDockedVisualizerInput).
 *    The legacy navigation refreshCB (Visualizer.handleRefresh) calls updateLocalStorage itself.
 * updateLocalStorage can't be called from a reducer (it calls store.getState()), but a store.subscribe listener runs
 * after the reducers have finished, so it's safe there.
 * The reducers are immutable, so a reference check is enough (and cheap - the listener runs on every dispatch).
 * Extra calls (e.g. canvasCount bumped by a zoom) cost nothing: the save is debounced, and skipped if nothing changed
 */
let jobLocalStorageSyncStarted = false;

export const startJobLocalStorageSync = () => {
  if (jobLocalStorageSyncStarted) {
    return
  }
  jobLocalStorageSyncStarted = true;
  const initialState = store.getState();
  let prevCartItems = initialState.cartState.cartItems;
  let prevAllWizardsValues = initialState.wizardState.allWizardsValues;
  let prevCanvasCount = initialState.bathroomVisualizerState.canvasCount;

  store.subscribe(() => {
    const {cartState, wizardState, bathroomVisualizerState} = store.getState();
    if ((cartState.cartItems === prevCartItems) && (wizardState.allWizardsValues === prevAllWizardsValues)
      && (bathroomVisualizerState.canvasCount === prevCanvasCount)) {
      return
    }
    prevCartItems = cartState.cartItems;
    prevAllWizardsValues = wizardState.allWizardsValues;
    prevCanvasCount = bathroomVisualizerState.canvasCount;
    updateLocalStorage(); // debounced, skipped while the save is blocked (restore question / restoring)
  });
  // not removed: the visualizer components unsubscribe on unmount, these events can be sent from other pages too
  $(window).on(`${ActionTypes.CHANGE_LAYOUT} ${ActionTypes.UPDATE_ALL_ITEMS}`, updateLocalStorage);
};

/**
 * Blocks/unblocks saving, while the restore question is shown and while the session is restored,
 * otherwise the stored session would be overwritten by the default state
 */
export const setJobLocalStorageSaveBlocked = (blocked: boolean) => {
  saveBlocked = blocked;
  if (blocked) {
    if (debounceTimer !== null) {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    saveGeneration++; // cancel the save in progress
  }
};

/**
 * call it when the stored session is removed, so the next save is not skipped as "nothing changed"
 */
export const resetJobLocalStorageLastSaved = () => {
  lastSavedValue = "";
};
